import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Home, Search, Map, Users, Zap } from "lucide-react";

const QUICK_LINKS = [
  { icon: Users, label: "Find Partners", href: "/find-partners" },
  { icon: Map, label: "Browse Venues", href: "/venues" },
  { icon: Search, label: "Fort Worth Players", href: "/us/texas/fort-worth" },
  { icon: Home, label: "Homepage", href: "/" },
];

const TOP_SPORTS = ["Tennis", "Pickleball", "Golf", "Table Tennis", "Skiing", "Padel"];

export default function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white pt-16 flex items-center justify-center px-4">
      <div className="max-w-xl w-full text-center">
        {/* 404 indicator */}
        <div className="flex items-center justify-center mb-6">
          <div className="w-16 h-16 bg-brand-green/10 rounded-2xl flex items-center justify-center">
            <Zap size={32} className="text-brand-green" />
          </div>
        </div>

        <p className="text-xs font-bold tracking-widest uppercase text-brand-green mb-3">404 — Page Not Found</p>
        <h1 className="text-5xl font-heading font-black text-white mb-4">That page moved, expired, or never existed.</h1>
        <p className="text-gray-500 mb-8">
          Don't worry — your next play partner is still out there. Use the quick links below to get back on court.
        </p>

        {/* Search */}
        <div className="relative mb-6">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" />
          <input
            type="text"
            placeholder="Search players, venues, or cities..."
            className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime/50"
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) {
                navigate(`/find-partners?city=${encodeURIComponent(e.target.value)}`);
              }
            }}
            data-testid="404-search-input"
          />
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          {QUICK_LINKS.map(({ icon: Icon, label, href }) => (
            <Link
              key={label}
              to={href}
              className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-600 hover:border-brand-green/40 hover:text-brand-green transition-colors"
              data-testid={`404-quick-link-${label.toLowerCase().replace(" ", "-")}`}
            >
              <Icon size={16} className="text-brand-green" />
              <span className="text-sm font-medium">{label}</span>
            </Link>
          ))}
        </div>

        {/* Sports */}
        <div className="text-left">
          <p className="text-xs font-bold tracking-widest uppercase text-gray-600 mb-3">Browse by Sport</p>
          <div className="flex flex-wrap gap-2">
            {TOP_SPORTS.map((s) => (
              <Link
                key={s}
                to={`/sports/${s.toLowerCase().replace(" ", "-")}`}
                className="px-3 py-1.5 bg-gray-50 border border-gray-200 text-sm text-gray-500 rounded-lg hover:border-brand-green/40 hover:text-brand-green transition-colors"
              >
                {s}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
